import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import _to_array from "@swc/helpers/src/_to_array.mjs";
import _to_consumable_array from "@swc/helpers/src/_to_consumable_array.mjs";
import { track as amplitudeTrack } from "@amplitude/analytics-browser";
import { Debug } from "@core/debug";
import { Scope, captureMessage } from "@sentry/react";
import { identify } from "./identify.js";
// Actions are sorted alphabetically - maintain this order when adding new ones
var availableAction = [
    "accepted",
    "added",
    "applied",
    "cancelled",
    "checked",
    "cleared",
    "clicked",
    "closed",
    "completed",
    "copied",
    "deleted",
    "disabled",
    "enabled",
    "expanded",
    "filtered",
    "loaded",
    "logout",
    "modified",
    "opened",
    "performed",
    "played",
    "received",
    "removed",
    "saved",
    "selected",
    "shared",
    "submitted",
    "switched",
    "unchecked",
    "viewed"
];
/**
 * Track customer actions.
 *
 * @param {TrackEvent|string} event
 * @param {TrackData} [data]
 *
 * @example ```typescript
 *   import {track} from '@core/tracking';
 *
 *   const trackComponent = (facetFields: ContentBrowserFacetFields) => {
 *     track('deleted content browser facet badge', {facet: facetFields});
 *   };
 *
 * @example ```typescript
 *   import {track} from '@core/tracking';
 *
 *   const trackComponent = (facetFields: ContentBrowserFacetFields) => {
 *     track({action: 'deleted', subject: 'content browser facet badge'}, {facet: facetFields});
 *   };
 * ```
 */ export var track = function(event) {
    var data = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    identify();
    var eventInput;
    if (typeof event !== "object") {
        var ref = _to_array(event.split(" ")), action = ref[0], subject = ref.slice(1);
        eventInput = {
            action: action,
            subject: subject.join(" ")
        };
    } else {
        eventInput = event;
    }
    var pathSegments = window.location.hash.split("?")[0].replace(/^#/, "").split("/").filter(Boolean);
    var _pathSegments = _sliced_to_array(pathSegments, 3), section = _pathSegments[1], subSection = _pathSegments[2];
    var location = "/" + pathSegments.slice(1).join("/");
    var pathParamValues = new Set(Object.values(data).map(String));
    var isDynamicSubSection = pathParamValues.has(subSection);
    var eventData = _object_spread_props(_object_spread({}, data), {
        section: section,
        location: location
    });
    if (subSection && !isDynamicSubSection) {
        eventData.subSection = subSection;
    }
    if (!availableAction.includes(eventInput.action)) {
        var scope = new Scope();
        scope.setTags({
            package: "@core/tracking",
            method: "track"
        });
        scope.setLevel("warning");
        scope.setContext("tracking", {
            event: "".concat(eventInput.action, " ").concat(eventInput.subject),
            eventData: eventData
        });
        captureMessage('[@core/tracking] The "'.concat(eventInput.action, '" action is not available in the list of usable actions "').concat(availableAction.join(","), '".'), scope);
    }
    amplitudeTrack("".concat(eventInput.action, " ").concat(eventInput.subject), eventData)// eslint-disable-next-line @helpers/no-then-catch-finally
    .promise.then(function(onfulfilled) {
        Debug.setState("trackingTool", function(prev) {
            var ref = _to_array(onfulfilled.event.event_type.split(" ")), action = ref[0], subject = ref.slice(1);
            var category = "action";
            if (action === "viewed") {
                category = "read";
            } else if ([
                "deleted",
                "removed"
            ].includes(action)) {
                category = "delete";
            } else if ([
                "added",
                "completed",
                "modified",
                "saved",
                "submitted"
            ].includes(action)) {
                category = "write";
            } else if (action === "loaded") {
                category = "system";
            }
            if (prev.length >= 25) {
                prev.pop();
            }
            return [
                {
                    action: action,
                    category: category,
                    event: onfulfilled.event,
                    legacyEvent: typeof event === "string",
                    subject: subject.join(" ")
                }
            ].concat(_to_consumable_array(prev));
        });
    })// eslint-disable-next-line @helpers/no-then-catch-finally
    .catch(function(error) {
        console.error("Tracking error:", error);
    });
};

//# sourceMappingURL=track.js.map